import React, { Fragment, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import validateEmailAddress from 'pruve/dist/validators/email';
import Button from '../components/Button/Button';
import { Input } from '../components/Input/Input';
import LoadingOverlay from '../components/LoadingOverlay/LoadingOverlay';
import { useRequestPasswordResetMutation } from '../lib/api/hooks/mutations/Auth/useRequestPasswordResetMutation';
import useScrollToError from '../lib/utils/hooks/useScrollToError';
import AnalyticsTracker from '../components/AnalyticsTracker/AnalyticsTracker';
import OpenRoute from '../lib/routes/OpenRoute';
import BackButton from '../components/BackButton/BackButton';
import useDevice from '../lib/utils/hooks/useDevice';
import useReCaptcha from '../lib/utils/hooks/useReCaptcha';
import FormError from '../components/FormError/FormError';

function RequestPasswordResetScreen() {
	const { handleSubmit, control, errors } = useForm();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	useScrollToError(errors);

	const requestPasswordResetMutation = useRequestPasswordResetMutation({
		onError: error => {
			setLoading(false);
			if (error?.response?.data === 'CAPTCHA validation failed')
				setError('Invalid captcha. Please refresh and try again');
		},
		onSuccess: () => {
			setLoading(false);
		},
	});

	const { ReCaptcha, captchaRef } = useReCaptcha();

	const onSubmit = (data: { email: string }) => {
		let captchaToken = captchaRef.getValue();
		if (!captchaToken) return setError('Please complete the captcha below.');

		setError('');
		setLoading(true);

		captchaRef.reset();
		requestPasswordResetMutation.mutate({
			email: data.email.trim(),
			captchaToken,
		});
	};

	const { isMobile } = useDevice();

	return (
		<div className='w-screen h-screen overflow-hidden flex'>
			{loading && <LoadingOverlay />}
			{isMobile === true && (
				<a
					href='https://www.bcorporation.net/en-us/'
					target='_blank'
					rel='noreferrer'
				>
					<img
						className='absolute top-2 right-2'
						src='/images/BCorpBlack.png'
						height='150'
						width='70'
						alt='bcorp logo'
					/>
				</a>
			)}
			<div className='overflow-x-hidden flex flex-col flex-1 items-center justify-center w-full h-full xl:min-w-[60vw] p-8 md:p-0'>
				<div className='flex flex-col items-start mx-12 w-full h-full max-w-lg justify-center'>
					<img
						className='w-auto h-10 mb-4'
						src='/images/logo-black.svg'
						alt="Stryve's Logo"
					/>
					<h3 className='text-3xl mb-6'>Reset your password</h3>
					{requestPasswordResetMutation.isSuccess && (
						<div className='w-full flex flex-col items-center justify-center p-8 text-slate-900'>
							<FontAwesomeIcon
								icon={faCheckCircle}
								size='3x'
								className='text-green-500'
							/>
							<h1 className='center text-xl subheading mt-4 '>
								We've sent you an email
							</h1>
							<p className='text-center mt-2'>Please check your inbox.</p>
						</div>
					)}
					{!requestPasswordResetMutation.isSuccess && (
						<form onSubmit={handleSubmit(onSubmit)} className='w-full max-w-lg'>
							<p className='flex text-slate-900 mb-4'>
								Enter the email address associated with your account and we'll
								send an email with instructions to reset your password.
							</p>
							{error && (
								<div className='my-4'>
									<FormError error={error} />{' '}
								</div>
							)}
							<Controller
								name='email'
								control={control}
								rules={{
									required: 'Please enter a valid email address',
									validate: {
										validEmail: value => {
											return (
												validateEmailAddress(value) ||
												'Please enter a valid email address'
											);
										},
									},
								}}
								className=''
								defaultValue=''
								render={props => {
									return (
										<Input
											type='text'
											onChange={value => {
												props.onChange(value);
											}}
											error={errors?.email?.message}
											extraClasses='text-slate-900 mb-4'
											label='Email address'
											value={props.value}
											name='email'
											placeholder='Please enter your email address'
											success={requestPasswordResetMutation.isSuccess}
											nullable={false}
											autoComplete
										/>
									);
								}}
							/>
							<div className='mt-4 mb-4'>
								<ReCaptcha />
							</div>
							<Button
								type='submit'
								onClick={() => {}}
								icon={faEnvelope}
								ariaLabel='Send Instructions'
								proceed
							>
								Send Instructions
							</Button>
						</form>
					)}
					<div className='mt-6'>
						<BackButton to='/' />
					</div>
				</div>
			</div>
			{isMobile === false && (
				<div
					className='lg:block relative flex-1 h-screen'
					style={{
						backgroundImage: `url('/images/backgrounds/loginBackground.svg')`,
						objectFit: 'fill',
						backgroundSize: 'cover',
					}}
				>
					<a
						href='https://www.stryve.online/b-corporation'
						target='_blank'
						rel='noreferrer'
					>
						<img
							className='absolute top-2 right-2'
							src='/images/BCorpWhite.png'
							height='150'
							width='70'
							alt='bcorp logo'
						/>
					</a>
				</div>
			)}
		</div>
	);
}

export default function ForgotPassword() {
	return (
		<Fragment>
			<AnalyticsTracker pageReference='FORGOT_PASSWORD' />
			<OpenRoute>
				<RequestPasswordResetScreen />
			</OpenRoute>
		</Fragment>
	);
}
